import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Loader2, ArrowLeft, Save } from 'lucide-react';
import { applicationsAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const ApplicationEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { language } = useLanguage();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [application, setApplication] = useState(null);
  const [formData, setFormData] = useState({
    startupName: '',
    website: '',
    description: '',
    stage: '',
    teamSize: '',
    fundingRaised: '',
    founderName: '',
    email: '',
    phone: ''
  });

  const t = {
    es: {
      title: 'Editar Postulación',
      back: 'Volver al Dashboard',
      program: 'Desafío',
      startupInfo: 'Información de la Startup',
      startupName: 'Nombre de la Startup',
      website: 'Sitio Web',
      description: 'Descripción',
      descriptionPlaceholder: 'Describe tu startup y cómo resuelve el desafío...',
      stage: 'Etapa',
      stages: {
        idea: 'Idea',
        mvp: 'MVP',
        early_stage: 'Etapa Temprana',
        growth: 'Crecimiento',
        scale: 'Escala'
      },
      teamSize: 'Tamaño del Equipo',
      fundingRaised: 'Financiamiento Recibido (USD)',
      founderInfo: 'Información del Fundador',
      founderName: 'Nombre del Fundador',
      email: 'Email',
      phone: 'Teléfono',
      save: 'Guardar Cambios',
      saving: 'Guardando...',
      successTitle: '¡Éxito!',
      successDesc: 'Tu postulación ha sido actualizada.',
      errorTitle: 'Error',
      errorDesc: 'No se pudo actualizar la postulación.',
      notAllowed: 'No tienes permiso para editar esta postulación.'
    },
    en: {
      title: 'Edit Application',
      back: 'Back to Dashboard',
      program: 'Challenge',
      startupInfo: 'Startup Information',
      startupName: 'Startup Name',
      website: 'Website',
      description: 'Description',
      descriptionPlaceholder: 'Describe your startup and how it solves the challenge...',
      stage: 'Stage',
      stages: {
        idea: 'Idea',
        mvp: 'MVP',
        early_stage: 'Early Stage',
        growth: 'Growth',
        scale: 'Scale'
      },
      teamSize: 'Team Size',
      fundingRaised: 'Funding Raised (USD)',
      founderInfo: 'Founder Information',
      founderName: 'Founder Name',
      email: 'Email',
      phone: 'Phone',
      save: 'Save Changes',
      saving: 'Saving...',
      successTitle: 'Success!',
      successDesc: 'Your application has been updated.',
      errorTitle: 'Error',
      errorDesc: 'Could not update the application.',
      notAllowed: 'You do not have permission to edit this application.'
    }
  }[language];

  useEffect(() => {
    fetchApplication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchApplication = async () => {
    try {
      setLoading(true);
      const data = await applicationsAPI.getById(id);
      setApplication(data);
      
      // Check if user owns this application (by userEmail or email if userEmail not set)
      const appUserEmail = data.userEmail || data.email;
      if (appUserEmail !== user?.email) {
        toast({
          title: t.errorTitle,
          description: t.notAllowed,
          variant: 'destructive'
        });
        navigate('/dashboard');
        return;
      }
      
      setFormData({
        startupName: data.startupName || '',
        website: data.website || '',
        description: data.description || '',
        stage: data.stage || '',
        teamSize: data.teamSize?.toString() || '',
        fundingRaised: data.fundingRaised?.toString() || '',
        founderName: data.founderName || '',
        email: data.email || '',
        phone: data.phone || ''
      });
    } catch (error) {
      console.error('Error fetching application:', error);
      toast({
        title: t.errorTitle,
        description: t.errorDesc,
        variant: 'destructive'
      });
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      await applicationsAPI.update(id, {
        startupName: formData.startupName,
        website: formData.website || null,
        description: formData.description,
        stage: formData.stage,
        teamSize: parseInt(formData.teamSize) || 1,
        fundingRaised: parseFloat(formData.fundingRaised) || 0,
        founderName: formData.founderName,
        email: formData.email,
        phone: formData.phone
      });

      toast({
        title: t.successTitle,
        description: t.successDesc,
      });

      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (error) {
      console.error('Error updating application:', error);
      toast({
        title: t.errorTitle,
        description: error.response?.data?.detail || t.errorDesc,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.back}
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">{t.title}</h1>
          <p className="text-gray-600 mt-1">
            {t.program}: <span className="font-semibold">{application?.programName}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Startup Information */}
          <Card className="border-2 border-gray-200 mb-6">
            <CardHeader>
              <CardTitle>{t.startupInfo}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="startupName">{t.startupName} *</Label>
                <Input
                  id="startupName"
                  name="startupName"
                  value={formData.startupName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="website">{t.website}</Label>
                <Input
                  id="website"
                  name="website"
                  type="url"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://..."
                />
              </div>

              <div>
                <Label htmlFor="description">{t.description} *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder={t.descriptionPlaceholder}
                  rows={4}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="stage">{t.stage} *</Label>
                  <Select 
                    value={formData.stage} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, stage: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t.stage} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="idea">{t.stages.idea}</SelectItem>
                      <SelectItem value="mvp">{t.stages.mvp}</SelectItem>
                      <SelectItem value="early_stage">{t.stages.early_stage}</SelectItem>
                      <SelectItem value="growth">{t.stages.growth}</SelectItem>
                      <SelectItem value="scale">{t.stages.scale}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="teamSize">{t.teamSize} *</Label>
                  <Input
                    id="teamSize"
                    name="teamSize"
                    type="number"
                    min="1"
                    value={formData.teamSize}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="fundingRaised">{t.fundingRaised}</Label>
                  <Input
                    id="fundingRaised"
                    name="fundingRaised"
                    type="number"
                    min="0"
                    value={formData.fundingRaised}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Founder Information */}
          <Card className="border-2 border-gray-200 mb-6">
            <CardHeader>
              <CardTitle>{t.founderInfo}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="founderName">{t.founderName} *</Label>
                <Input
                  id="founderName"
                  name="founderName"
                  value={formData.founderName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">{t.email} *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="phone">{t.phone} *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end">
            <Button 
              type="submit" 
              disabled={saving}
              className="bg-purple-600 text-white hover:bg-purple-700"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t.saving}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {t.save}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApplicationEdit;
